/**
 * F-030/F-031 — the canonical 4-state document lifecycle. `deriveDocState`
 * collapses the raw operational `documents.status` (pending/assigned/in_progress/
 * completed/held/escalated/rejected/error/exported/complete + voided) onto ONE
 * vocabulary that Queues, Batches, and badges all read from. State depends only
 * on `status` (it is orthogonal to `currentStep`, per the design); the step is
 * used only for the human label (see `deriveDocStateLabel`).
 *
 * Design: docs/superpowers/specs/2026-06-30-status-model-redesign-design.md
 */
/** The four LIVE states. `voided` is derived separately and is NOT live work. */
export const DOC_STATES = ['ready', 'busy', 'error', 'complete'];
/**
 * The raw statuses that map to each state. Single source for both the runtime
 * derivation AND for building DB queries (`statusesForState`) so a query filter
 * can never drift from `deriveDocState`. NOTE: `error` also absorbs any UNKNOWN
 * status at derivation time, but that list is open-ended, so `STATUSES_BY_STATE.error`
 * lists only the KNOWN error statuses (queries should not need the unknown ones).
 */
const STATUSES_BY_STATE = {
    // `busy` = actively locked to a user or a process, i.e. work is happening on
    // the doc RIGHT NOW: `in_progress` (active keying / the actual export-write)
    // and `processing` (OCR/classify running). Docs merely SITTING in a queue are
    // NOT busy: `assigned` (claimed but not opened) and `completed` (keyed,
    // awaiting advance) are `ready`. The board layers a live-Redis-lock check on
    // top of this (an open doc is busy even if its status hasn't flipped yet).
    ready: ['pending', 'ready', 'assigned', 'completed'],
    busy: ['in_progress', 'processing'],
    error: ['error', 'held', 'escalated', 'rejected'],
    complete: ['exported', 'complete'],
    voided: ['split', 'merged', 'deleted'],
};
const READY = new Set(STATUSES_BY_STATE.ready);
const BUSY = new Set(STATUSES_BY_STATE.busy);
const ERROR = new Set(STATUSES_BY_STATE.error);
const COMPLETE = new Set(STATUSES_BY_STATE.complete);
const VOIDED = new Set(STATUSES_BY_STATE.voided);
/**
 * The known raw statuses for a given state — for building DB `where status in
 * (...)` filters from the same source as `deriveDocState`. (For `error`, only
 * the known statuses; unknown statuses also derive to `error` but can't be
 * enumerated.)
 */
export function statusesForState(state) {
    return STATUSES_BY_STATE[state];
}
/** Map a raw `documents.status` to the canonical 4-state (+ voided). */
export function deriveDocState(status) {
    if (VOIDED.has(status))
        return 'voided';
    if (READY.has(status))
        return 'ready';
    if (BUSY.has(status))
        return 'busy';
    if (COMPLETE.has(status))
        return 'complete';
    if (ERROR.has(status))
        return 'error';
    // Unknown → error so a mis-set status surfaces loudly instead of vanishing.
    return 'error';
}
/** True for the four live states; false for voided. */
export function isLiveState(state) {
    return state !== 'voided';
}
/**
 * F-085 — statuses meaning "an operator currently holds/claims this doc".
 * Drives the workability axis (`being_worked`) on batch rows. Single source
 * for the API list endpoint's `assignedCount` and the web BatchDetailPage's
 * client-side derivation, so the two sides of the composed
 * "<state> — <workability>" badge phrase can never drift.
 */
export const CLAIMED_DOC_STATUSES = ['assigned', 'in_progress'];
const CLAIMED = new Set(CLAIMED_DOC_STATUSES);
/** True when a raw `documents.status` means an operator has claimed the doc. */
export function isClaimedDocStatus(status) {
    return CLAIMED.has(status);
}
/** Step -> present-tense verb phrase for the `busy` state. */
const BUSY_VERB = {
    scan: 'Scanning',
    classify: 'Classifying',
    key: 'Keying in progress',
    verify: 'Verifying',
    qc: 'QC in progress',
    supervisor: 'Supervisor review',
    export: 'Exporting',
};
/** Step -> imperative for the `ready` state. Falls back to the bare "Ready". */
const READY_VERB = {
    scan: 'Ready to scan',
    classify: 'Ready to classify',
    key: 'Ready to key',
    verify: 'Ready to verify',
    qc: 'Ready for QC',
    supervisor: 'Ready for supervisor',
    export: 'Ready to export',
};
/**
 * Step-agnostic label for a state — the canonical 4-state vocabulary used by the
 * reconciled summary bar and the batch badge (which summarize across steps, so
 * they can't use the step-scoped `deriveDocStateLabel`). Single source for the
 * `deriveDocStateLabel` fallbacks too, so the two never drift.
 */
export function docStateLabel(state) {
    switch (state) {
        case 'ready':
            return 'Ready';
        case 'busy':
            return 'In progress';
        case 'error':
            return 'Needs attention';
        case 'complete':
            return 'Complete';
    }
}
/**
 * Human label for a (step, state) pair — the single home for the strings the
 * badges/queues used to hand-maintain ("Keying in Progress", "Exporting", …).
 */
export function deriveDocStateLabel(step, state) {
    switch (state) {
        case 'busy':
            return BUSY_VERB[step] ?? docStateLabel('busy');
        case 'ready':
            return READY_VERB[step] ?? docStateLabel('ready');
        case 'error':
        case 'complete':
            return docStateLabel(state);
    }
}
//# sourceMappingURL=doc-state.js.map